import { List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { useMemo } from 'react';
import { DynamicIcon } from 'src/components';
import {
  PasswordsValidation,
  PasswordValidationMessages,
  PasswordValidationRuleNames,
} from './types';

const VALID_TEXT_COLOR = '#19BF00';
const INVALID_TEXT_COLOR = '#DF3312';
const VALID_ICON_NAME = 'check';
const INVALID_ICON_NAME = 'clear';

export const ResendInvitePasswordValidation = ({
  validationResults,
}: {
  validationResults: PasswordsValidation | null;
}) => {
  const validationMessages = useValidationMessages(validationResults);

  return validationResults ? (
    <List>
      {Object.keys(validationMessages).map((key) => {
        const messageConfig = validationMessages[key as keyof PasswordValidationMessages];
        const color = messageConfig.isValid ? VALID_TEXT_COLOR : INVALID_TEXT_COLOR;
        const icon = messageConfig.isValid ? VALID_ICON_NAME : INVALID_ICON_NAME;
        return (
          <ListItem
            sx={{
              padding: 0,
              color,
            }}
            key={key}
          >
            <ListItemIcon sx={{ minWidth: '2rem', color }}>
              <DynamicIcon name={icon} />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ fontSize: '1rem!important' }}
              primary={messageConfig.text}
            />
          </ListItem>
        );
      })}
    </List>
  ) : null;
};

function useValidationMessages(
  validationResults: PasswordsValidation | null,
): PasswordValidationMessages {
  const passwordValidation = useMemo(
    () => ({
      [PasswordValidationRuleNames.atLeastTenCharacters]: {
        text: '10-character minimum length',
        isValid: !!validationResults?.[PasswordValidationRuleNames.atLeastTenCharacters],
      },
      [PasswordValidationRuleNames.atLeastOneNumber]: {
        text: 'Contains at least 1 number',
        isValid: !!validationResults?.[PasswordValidationRuleNames.atLeastOneNumber],
      },
      [PasswordValidationRuleNames.atLeastOneLowerCaseLetter]: {
        text: 'Contains at least 1 lowercase letter',
        isValid: !!validationResults?.[PasswordValidationRuleNames.atLeastOneLowerCaseLetter],
      },
      [PasswordValidationRuleNames.atLeastOneUpperCaseLetter]: {
        text: 'Contains at least 1 uppercase letter',
        isValid: !!validationResults?.[PasswordValidationRuleNames.atLeastOneUpperCaseLetter],
      },
      [PasswordValidationRuleNames.passwordsEqual]: {
        text: 'Passwords must match',
        isValid: !!validationResults?.[PasswordValidationRuleNames.passwordsEqual],
      },
      [PasswordValidationRuleNames.atLeastOneSpecialCharacter]: {
        text: 'Contains at least 1 special character from the following set, or a non-leading, non-trailing space character',
        isValid: !!validationResults?.[PasswordValidationRuleNames.atLeastOneSpecialCharacter],
      },
    }),
    [validationResults],
  );

  return passwordValidation;
}
